import React from 'react';
import Box from "@mui/material/Box";
import {Button} from "@mui/material";
import {Link, Typography} from "@mui/joy";
import {ChevronRightSharp, SearchOffRounded, SearchRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import SearchAssetEmptyView from "../emptyView";
import {ProductBrowser, CatalogProductNode, EmptySearchContext} from "@dreebit-registry/vsm-microservice-components";
import AssetProductImage from "../../AssetProductImage";
import productNamePlate from "../../../assets/img/product-name-plate.svg";

// Only published nodes whose product category allows asset creation are offered.
const CATALOG_LIST_FILTER = { status: "published", assetCreationEnabled: { equals: true } };

interface Props {
    selectedProduct?: CatalogProductNode | null;
    /** Selection changed inside the browser (no step advance). */
    onSelectionChange: (product: CatalogProductNode | null) => void;
    /** Commit the selection and advance to the create-asset form. */
    onProductSelect: (product: CatalogProductNode) => void;
    /** Create an asset without a catalog product (manual manufacturer path). */
    onCreateManually?: () => void;
    /** Offer the browser's help toggle with the how-to tutorial (hidden in the replacement embed). */
    showHelp?: boolean;
}

/** The how-to steps shown by the browser's help toggle (in place of the browser body). */
const ProductBrowserTutorial = () => {
    const {t} = useTranslation();
    const steps = [
        {title: t('Choose a product family'), caption: t('Or search directly by name or product code')},
        {title: t('Narrow down step by step'), caption: t('Click into the matching series or model')},
        {title: t('Select your product as precisely as possible'), caption: t('The more precise your pick, the better')},
    ];
    return (
        // Sized to fit the browser's list slot without scrolling (~320px at the
        // default 560px step budget) — keep it tight when adding content here.
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '13px'}}>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'}}>
                <Box
                    component={'img'}
                    src={productNamePlate}
                    alt={t('Example nameplate with the product name highlighted')}
                    sx={{width: '100%', maxWidth: '275px', height: 'auto'}}
                />
                <Typography level={'bodySmall'} sx={{color: 'text.secondary', textAlign: 'center'}}>
                    {t('Tip: You can find the product name on the nameplate.')}
                </Typography>
            </Box>
            {steps.map((step, index) => (
                // Circle centered against the title+caption block, not just the title
                <Box key={index} sx={{display: 'flex', alignItems: 'center', gap: '13px', minWidth: 0}}>
                    <Box sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 600,
                        flexShrink: 0,
                    }}>
                        {index + 1}
                    </Box>
                    <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
                        <Typography level={'titleSmall'}>{step.title}</Typography>
                        <Typography level={'bodySmall'} sx={{color: 'text.secondary'}}>{step.caption}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const ProductSearch = ({selectedProduct, onSelectionChange, onProductSelect, onCreateManually, showHelp}: Props) => {

    const {t} = useTranslation();

    return <Box className={'flex-column'} sx={{flexGrow: 1, gap: '13px', minHeight: 0, minWidth: 0, maxWidth: '100%'}}>
        <ProductBrowser
            value={selectedProduct}
            onChange={onSelectionChange}
            fillHeight
            baseFilter={CATALOG_LIST_FILTER}
            helpContent={showHelp ? <ProductBrowserTutorial/> : undefined}
            renderImage={(node) => (
                <AssetProductImage
                    src={node.image?.thumbnailLink}
                    style={{width: 32, height: 32, borderRadius: 4, flexShrink: 0}}
                />
            )}
            emptySearchContent={(ctx: EmptySearchContext) =>
                ctx.scope === 'scoped' ? (
                    // A scoped miss is not proof the product doesn't exist — offer
                    // widening to the whole catalog before the manual fallback.
                    <SearchAssetEmptyView
                        icon={<SearchOffRounded/>}
                        title={t("No results below '{{name}}'.", {name: ctx.parentName})}
                        subtitle={t('The product may be located elsewhere in the catalog.')}
                        extra={
                            <Box sx={{display: 'flex', gap: '13px', flexWrap: 'wrap', justifyContent: 'center'}}>
                                <Button variant={'outlined'} startIcon={<SearchRounded/>} onClick={ctx.searchGlobally}>
                                    {t('Search entire catalog')}
                                </Button>
                                {onCreateManually && (
                                    <Button variant={'outlined'} onClick={onCreateManually}>
                                        {t('Create manually')}
                                    </Button>
                                )}
                            </Box>
                        }
                    />
                ) : (
                    <SearchAssetEmptyView
                        icon={<SearchOffRounded/>}
                        title={t("We couldn't find a matching product.")}
                        subtitle={t('Use "Create manually" to add the product manually.')}
                    />
                )
            }
        />
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '13px'}}>
            {onCreateManually ? (
                <Typography level={'bodySmall'} sx={{textAlign: 'left'}}>
                    {t("Can't find your product?")}{' '}
                    <Link component={'button'} onClick={onCreateManually}>
                        {t('Create manually')}
                    </Link>
                </Typography>
            ) : <Box/>}
            <Button
                variant="contained"
                endIcon={<ChevronRightSharp/>}
                disabled={!selectedProduct}
                onClick={() => selectedProduct && onProductSelect(selectedProduct)}
            >
                {t('Next')}
            </Button>
        </Box>
    </Box>;
};

export default ProductSearch;
